/**
 * Engagement-tab TanStack Query hooks (Plan 06 Task 6.2).
 *
 * One hook per endpoint, each unwrapping the server envelope and returning
 * the inner payload. Query keys start with `['engagement', <endpoint>,
 * …, filtersQuestion(filters)]` so the TanStack cache is keyed by the semantic
 * filter set (not the raw URL) and cache hits survive cosmetic URL changes.
 *
 * `staleTime: 60_000` + `placeholderData: keepPreviousData` — and since the
 * recount rule, the second of those no longer means the previous filter set's
 * answer stays on screen. `useFilteredQuery` reports that window as "nothing
 * counted yet" (hooks/use-filtered-query.ts), so the tab blanks and says so.
 * What `keepPreviousData` still buys: telling a recount apart from a first
 * load, and an instant switch back to a filter set already in the cache.
 */

import { keepPreviousData } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import {
  getActivityHeatmap,
  getCohortDrilldown,
  getCohortHeatmap,
  getDauTrend,
  getEngagementKpis,
  getJiraActivityTimeline,
  getTopUsers,
  getUsersTrendBreakdown,
  type TopUsersParams,
} from '@/services/engagement-api';
import type {
  ActivityHeatmapResponse,
  CohortDrilldownResponse,
  CohortHeatmapResponse,
  DauTrendPoint,
  EngagementKpis,
  JiraActivityPoint,
  TopUserRow,
  UsersTrendBreakdown,
  UsersTrendPoint,
} from '@/services/api-types';
import { getUsersTrend } from '@/services/engagement-api';

const STALE_TIME = 60_000;

export function useEngagementKpis(filters: Filters) {
  return useFilteredQuery<EngagementKpis>({
    queryKey: ['engagement', 'kpis', filtersQuestion(filters)],
    queryFn: async () => (await getEngagementKpis(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useUsersTrend(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useFilteredQuery<UsersTrendPoint[]>({
    queryKey: [
      'v2',
      'engagement',
      'users-trend',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getUsersTrend(filters, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useDauTrend(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useFilteredQuery<DauTrendPoint[]>({
    queryKey: [
      'v2',
      'engagement',
      'dau-trend',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getDauTrend(filters, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useCohortHeatmap(filters: Filters) {
  return useFilteredQuery<CohortHeatmapResponse>({
    queryKey: ['engagement', 'cohort-heatmap', filtersQuestion(filters)],
    queryFn: async () => (await getCohortHeatmap(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useActivityHeatmap(filters: Filters) {
  return useFilteredQuery<ActivityHeatmapResponse>({
    queryKey: ['engagement', 'activity-heatmap', filtersQuestion(filters)],
    queryFn: async () => (await getActivityHeatmap(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Jira-prefill activity timeline — per-bucket {generated, clicked} counts
 * rendered by `<JiraActivityTimeline>` on the Engagement tab. Participates
 * in the same `staleTime` + `keepPreviousData` cadence as the rest of the
 * tab so filter changes don't blank the chart.
 */
export function useJiraActivityTimeline(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useFilteredQuery<JiraActivityPoint[]>({
    queryKey: [
      'v2',
      'engagement',
      'jira-activity-timeline',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () =>
      (await getJiraActivityTimeline(filters, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useTopUsers(filters: Filters, params: TopUsersParams = {}) {
  /* `sort` blanks here: the server sorts and then keeps the top 100, so a
     different sort is a different SET of users (owner's ruling 3). */
  return useFilteredQuery<TopUserRow[]>({
    queryKey: [
      'v2',
      'engagement',
      'top-users',
      params.limit ?? null,
      params.sort ?? null,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getTopUsers(filters, params)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Users-trend breakdown — backs the <UsersTrendBreakdownModal>.
 *
 * The hook is inert when `bucket === null` (no modal open) — `enabled`
 * short-circuits the fetch and the consumer can read `data` freely
 * without guarding. Query key includes the bucket + granularity so
 * opening different bars doesn't reuse each other's payloads.
 */
export function useUsersTrendBreakdown(
  bucket: string | null,
  granularity: Filters['granularity'],
  filters: Filters,
) {
  return useFilteredQuery<UsersTrendBreakdown>({
    queryKey: [
      'v2',
      'engagement',
      'users-trend-breakdown',
      bucket,
      granularity,
      filtersQuestion(filters),
    ],
    // `enabled` gates the fetch; `queryFn` is never invoked with a null
    // bucket, so the non-null assertion is safe here.
    enabled: bucket !== null,
    queryFn: async () =>
      (await getUsersTrendBreakdown(filters, bucket as string, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Cohort-heatmap cell drill-down — backs `<CohortDrilldownModal>`.
 *
 * Inert when `cohortWeek === null` (modal closed). Same `enabled` gating
 * pattern as `useUsersTrendBreakdown` — opening different cells keys the
 * cache independently so toggling cells doesn't reuse a stale payload.
 */
export function useCohortDrilldown(
  cohortWeek: string | null,
  weekOffset: number | null,
  filters: Filters,
) {
  const enabled = cohortWeek !== null && weekOffset !== null;
  return useFilteredQuery<CohortDrilldownResponse>({
    queryKey: [
      'v2',
      'engagement',
      'cohort-drilldown',
      cohortWeek,
      weekOffset,
      filtersQuestion(filters),
    ],
    enabled,
    queryFn: async () =>
      (
        await getCohortDrilldown(
          filters,
          cohortWeek as string,
          weekOffset as number,
        )
      ).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}
